// Centralised RUNTIME product resolution.
//
// ONE Cloud Run image now serves every product (scm/crm/ops/mt/mro, soon g2).
// Which product a given page belongs to is decided at runtime from
// window.location.hostname instead of being baked in at build time via
// REACT_APP_CUSTOMER / per-product Cloud Build images. This is the frontend
// half of the "one image, one DB" consolidation
// (repo/brain/projects/2026-06-27-gcp-cost-review-ship-status.md).
//
// Every value below is PUBLIC (API URLs, Cognito hosted-UI client ids, redirect
// URIs). No secrets ship in the bundle — these are the same values that were
// previously passed as Cloud Build --build-arg substitutions.

export type Product =
  | "scm"
  | "crm"
  | "ops"
  | "mt"
  | "mro"
  | "g2"
  | "gch"
  | "volcanic"
  | "aquipe"
  | "demo"
  | "mustang"
  | "peak"
  | "synergy";

const PRODUCTS: readonly Product[] = [
  "scm",
  "crm",
  "ops",
  "mt",
  "mro",
  "g2",
  "gch",
  "volcanic",
  "aquipe",
  "demo",
  "mustang",
  "peak",
  "synergy",
];

/**
 * Identity provider backing a tenant's login.
 *
 * Every tenant through 2026-08 authenticated against AWS Cognito. Aquipe is the
 * first on Google Cloud Identity Platform (GCIP): it is a greenfield PRODUCTION
 * onboarding with zero existing users, so it is the only tenant that can adopt
 * GCIP without forcing a password reset (Cognito will not export password
 * hashes). Existing tenants stay on Cognito and their code path is unchanged —
 * `authProvider` is omitted for them and defaults to "cognito".
 */
export type AuthProvider = "cognito" | "gcip";

/**
 * Brand a tenant renders under, resolved at RUNTIME from the host.
 *
 * Brand used to be decided at BUILD time — the accent by `REACT_APP_NEW_THEME`
 * and the header logo by `REACT_APP_LOGO`, both baked into the image. That was
 * workable while every product shipped its own build; it is not, now that one
 * image serves every tenant, because a build-time value cannot differ per host.
 * Aquipe is the first tenant to carry its own identity into the app itself.
 *
 * Tenants without a `brand` fall back to the historic Aeronet teal + banner, so
 * the eight existing tenants are unaffected.
 */
export type BrandName = "aeronet" | "aquipe" | "g2";

export interface Brand {
  /** Which exported MUI theme to mount (see src/theme.ts). */
  theme: "teal" | "blue" | "orange";
  /** Accent hex — kept alongside the theme name for non-MUI surfaces (SVG marks). */
  accent: string;
  /** Header logo. `svg` renders the inline tenant mark; `asset` uses a public/ file. */
  logo: { kind: "svg"; mark: BrandName } | { kind: "asset"; file: string };
  /** Wordmark shown beside the mark, when the logo is an inline svg. */
  wordmark?: string;
}

const BRANDS: Record<BrandName, Brand> = {
  aeronet: {
    theme: "teal",
    accent: "#008e92",
    logo: { kind: "asset", file: "aeronet_banner_2022.png" },
  },
  aquipe: {
    theme: "orange",
    accent: "#C0551E",
    logo: { kind: "svg", mark: "aquipe" },
    wordmark: "AQUIPE",
  },
  g2: {
    theme: "blue",
    accent: "#0A60A2",
    logo: { kind: "asset", file: "g2_logo.png" },
    wordmark: "G2 AVIATION",
  },
};

// Cognito pool + hosted-UI domain are SHARED across every product since the SCM
// auth-pool consolidation (2026-06-27): one survivor pool 4fxBupyoB, domain
// aeronet-crm. Only the per-product app *client id* (and the registered
// redirect URIs) differ — see PRODUCT_CONFIG below.
export const COGNITO_REGION = "ap-southeast-2";
export const COGNITO_USER_POOL_ID = "ap-southeast-2_4fxBupyoB";
export const COGNITO_DOMAIN =
  "aeronet-crm.auth.ap-southeast-2.amazoncognito.com";

interface BaseProductConfig {
  // Tenant tag sent to the backend as the X-Install-Name header, and used for
  // telemetry (Sentry env), the document title, and the per-tenant logo path.
  // NOTE: SCM's install tag is historically "aeronet", NOT "scm".
  installName: string;
  // Registered callback / logout URIs. Stored explicitly (not derived from
  // origin) so they match the IdP's allowed-URL list byte-for-byte — SCM's
  // logout URI has no ?function=log_out query, the others do.
  redirectSignIn: string;
  redirectSignOut: string;
  // Optional explicit API base. Almost every product derives it as
  // `https://api.<product>.aeronet.dev`; set this only when a product's API is
  // reached at a non-standard origin (e.g. g2 — a customer demo tenant whose
  // own-DB API runs on a Cloud Run run.app URL; no api.g2.aeronet.dev, no cert roll).
  apiUrl?: string;
  /** Optional Data Views API base. If omitted, uses REACT_APP_DATA_VIEW_URL or relative. */
  dataViewUrl?: string;
  /** Tenant brand. Absent = the historic Aeronet identity (teal + banner). */
  brand?: BrandName;
  /**
   * Display label for the Hub, overriding the build-time alias.
   *
   * The historic label came from `REACT_APP_CUSTOMER` (see src/hub/hubLabel.ts),
   * which is baked into the image at build time. That was fine while each
   * product shipped its own build; it is not, now that ONE image serves every
   * tenant from the same bundle — `.env.production` pins it to "aeronet", so
   * every tenant reads the same label and no build-time value can differ per
   * host.
   *
   * This override is resolved at RUNTIME from the hostname, so it renames ONE
   * tenant's Hub without touching any other. Deliberately opt-in: a tenant that
   * omits it keeps the build-time behaviour byte-for-byte, so the eight
   * pre-existing tenants are unaffected.
   */
  hubLabel?: string;
  /**
   * A demo tenant, whatever its zone. Absent = decided by host: *.aeronet.dev is demo, *.aeronet.app is not.
   * Set it only where that rule is wrong (a demo on .app). Drives the wake screen's wording (src/app/wake.ts).
   */
  demo?: boolean;
}

interface CognitoProductConfig extends BaseProductConfig {
  // Omitted on every pre-existing tenant; absence means "cognito" so those
  // entries stay byte-identical to their pre-GCIP form.
  authProvider?: "cognito";
  // Public Cognito hosted-UI app client id for this product's SPA.
  cognitoClientId: string;
  // Optional per-product Cognito pool + hosted-UI domain. Almost every product
  // shares the survivor pool (COGNITO_USER_POOL_ID / COGNITO_DOMAIN below); set
  // these only when a tenant authenticates against its OWN pool (e.g. gch — the
  // .dev demo shares its live install's pool `gch-prod`/`aeronet-gch` so real
  // GCH users log in with existing credentials).
  userPoolId?: string;
  cognitoDomain?: string;
  /**
   * AWS region of this tenant's pool. Absent = COGNITO_REGION (ap-southeast-2),
   * which is every tenant on the survivor pool and the two that share their own
   * ap-southeast-2 install pools (gch, volcanic). Set ONLY for a pool in another
   * region — g2 (ca-central-1) and synergy (us-east-1), whose live install pools
   * live there. Without this the SPA would aim such a pool id at an
   * ap-southeast-2 endpoint.
   */
  cognitoRegion?: string;
  /**
   * Cognito IDENTITY pool (federated AWS credentials), distinct from the USER
   * pool above. The build-time REACT_APP_COGNITO_IDENTITY_POOL_ID is an
   * ap-southeast-2 pool; a tenant whose user pool lives elsewhere needs its
   * own, because an identity pool only trusts user pools in its own region.
   */
  cognitoIdentityPoolId?: string;
}

interface GcipProductConfig extends BaseProductConfig {
  authProvider: "gcip";
  // Firebase/GCIP web API key + auth domain. PUBLIC, like the Cognito client
  // ids above — a GCIP API key identifies the project, it does not authorise
  // anything on its own.
  gcipApiKey: string;
  gcipAuthDomain: string;
  // Set only when the tenant is an isolated GCIP *tenant* under Identity
  // Platform multi-tenancy rather than a bare project-level user directory.
  gcipTenantId?: string;
}

// Discriminated on `authProvider` so a Cognito tenant cannot silently omit its
// client id and a GCIP tenant cannot silently omit its API key.
type ProductConfig = CognitoProductConfig | GcipProductConfig;

// ⚠️ PEAK P2b PLACEHOLDER (2026-09-26). The Cloud Run service does not exist
// yet; DevOps creates it in install phase P2b. Replace this constant with the
// real URL, then delete this block, before peak.aeronet.dev takes its first
// login. The value is PREDICTED from Cloud Run's deterministic URL form
// (https://<service>-<project-number>.<region>.run.app, the form gch and
// volcanic use), and is UNVERIFIED until `gcloud run services describe
// aeronet-fast-api-peak --region=australia-southeast1
// --format='value(status.url)'` answers with it.
const PEAK_API_URL_P2B_PREDICTED =
  "https://aeronet-fast-api-peak-110933780770.australia-southeast1.run.app";

// ⚠️ SYNERGY PLACEHOLDER (2026-10-10), the same shape as peak's above. The Cloud
// Run service does not exist yet (provisioning plan step 12). The value is
// PREDICTED from Cloud Run's deterministic URL form and is UNVERIFIED until
// `gcloud run services describe aeronet-fast-api-synergy
// --region=australia-southeast1 --format='value(status.url)'` answers with it.
// Confirm or replace it before synergy.aeronet.dev takes its first login.
const SYNERGY_API_URL_PREDICTED =
  "https://aeronet-fast-api-synergy-110933780770.australia-southeast1.run.app";

const PRODUCT_CONFIG: Record<Product, ProductConfig> = {
  scm: {
    installName: "aeronet",
    cognitoClientId: "3b1oo6tsejhbg22q8lvj040o7a",
    redirectSignIn: "https://scm.aeronet.dev/app/",
    redirectSignOut: "https://scm.aeronet.dev/app/",
  },
  crm: {
    // CRM — PRODUCTION customer tenant on .app zone (alongside Aquipe).
    installName: "crm",
    cognitoClientId: "3nqlhelt9u0blbqdmesiv162o",
    redirectSignIn: "https://crm.aeronet.app/app/",
    redirectSignOut: "https://crm.aeronet.app/app/?function=log_out",
    apiUrl: "https://api.crm.aeronet.app",
  },
  ops: {
    installName: "ops",
    cognitoClientId: "2sg04f52bpa58r0jmfu3g5ln9d",
    redirectSignIn: "https://ops.aeronet.dev/app/",
    redirectSignOut: "https://ops.aeronet.dev/app/?function=log_out",
  },
  mt: {
    installName: "mt",
    cognitoClientId: "251tjvb2o7o6ikeqje4lug1vs",
    redirectSignIn: "https://mt.aeronet.dev/app/",
    redirectSignOut: "https://mt.aeronet.dev/app/?function=log_out",
  },
  mro: {
    installName: "mro",
    cognitoClientId: "72mp0s55coj7gj1or78icp5qh9",
    redirectSignIn: "https://mro.aeronet.dev/app/",
    redirectSignOut: "https://mro.aeronet.dev/app/?function=log_out",
  },
  demo: {
    // demo.aeronet.dev — Consolidated demo environment unifying all 5 core products
    // (SCM, CRM, OPS, MT, MRO) using the G2 menu structure.
    installName: "demo",
    cognitoClientId: "3b1oo6tsejhbg22q8lvj040o7a",
    redirectSignIn: "https://demo.aeronet.dev/app/",
    redirectSignOut: "https://demo.aeronet.dev/app/?function=log_out",
    apiUrl: "https://api.demo.aeronet.dev",
    hubLabel: "Aeronet Hub",
  },
  g2: {
    // G2 Aviation — a customer DEMO tenant (prospect, opp #109).
    // No `brand`: g2 renders the standard Aeronet identity (teal + banner
    // logo) — Aaron 2026-08-22: the only install with custom branding is
    // aquipe. BRANDS.g2 stays defined for a future re-enable.
    installName: "g2",
    // Aaron 2026-08-24: "rename the hub G2 Hub". Set HERE rather than in
    // hubLabel.ts so it applies to the g2 host only — a literal in hubLabel.ts
    // would rename the Hub for FlightCare, aquipe and every other tenant on
    // this shared image.
    hubLabel: "G2 Hub",
    // Aaron 2026-09-16: moved OFF GCIP, onto G2's own live install pool
    // `g2-prod` (ca-central-1) — the same pattern as gch/volcanic, so G2 staff
    // sign in to the .dev demo with their real g2.aeronet.nz credentials.
    // ca-central-1 is why `cognitoRegion` exists: this is the first tenant
    // whose pool is NOT in ap-southeast-2.
    userPoolId: "ca-central-1_HjqykXSsn",
    cognitoDomain: "aeronet-g2.auth.ca-central-1.amazoncognito.com",
    cognitoClientId: "47bh7vmjdpdl810rt4jju2vqrt",
    cognitoRegion: "ca-central-1",
    cognitoIdentityPoolId: "ca-central-1:e3108493-7c9b-43b1-bada-bc881484d09c",
    redirectSignIn: "https://g2.aeronet.dev/app/",
    redirectSignOut: "https://g2.aeronet.dev/app/?function=log_out",
    apiUrl: "https://aeronet-fast-api-g2-bjtmq2eija-ts.a.run.app",
    dataViewUrl: "https://aeronet-data-views-g2-bjtmq2eija-ts.a.run.app/data/view",
  },
  gch: {
    // Glacier Country Helicopters — a customer DEMO/onboarding tenant (onsite
    // w/c 20 Jul). Own database (module_gch_demo, cloned from their live AWS
    // module_gch) + own API service (aeronet-fast-api-gch), per-tenant model
    // like g2 — so its API base is set explicitly (no api.gch.aeronet.dev).
    installName: "gch",
    // gch shares its LIVE install's Cognito pool (gch-prod / aeronet-gch) so
    // real GCH users log into the demo with their existing credentials — NOT
    // the shared survivor pool. Dedicated .dev app client on that pool.
    userPoolId: "ap-southeast-2_Yyb2O1Vo5",
    cognitoDomain: "aeronet-gch.auth.ap-southeast-2.amazoncognito.com",
    cognitoClientId: "7l6ph7229jb4qtibbeah0qht25",
    redirectSignIn: "https://gch.aeronet.dev/app/",
    redirectSignOut: "https://gch.aeronet.dev/app/?function=log_out",
    apiUrl: "https://aeronet-fast-api-gch-110933780770.australia-southeast1.run.app",
  },
  volcanic: {
    // Volcanic Air — a customer DEMO/TEST tenant, used to exercise the newly
    // ported v6 modules against real operator data + role-based menus. Own
    // database (module_volcanic_demo, cloned from their live AWS
    // module_volcanic) + own API service (aeronet-fast-api-volcanic),
    // per-tenant model like gch — so its API base is set explicitly (no
    // api.volcanic.aeronet.dev).
    installName: "volcanic",
    // volcanic shares its LIVE install's Cognito pool (volcanic-prod /
    // aeronet-volcanic) so real Volcanic staff log into the test stack with
    // their existing credentials — NOT the shared survivor pool. Dedicated
    // .dev app client on that pool.
    userPoolId: "ap-southeast-2_MXBlyLATT",
    cognitoDomain: "aeronet-volcanic.auth.ap-southeast-2.amazoncognito.com",
    cognitoClientId: "bdd4l6r3103hl8tnkfo4vvvj4",
    redirectSignIn: "https://volcanic.aeronet.dev/app/",
    redirectSignOut: "https://volcanic.aeronet.dev/app/?function=log_out",
    apiUrl:
      "https://aeronet-fast-api-volcanic-110933780770.australia-southeast1.run.app",
  },
  aquipe: {
    // Aquipe (AECS Equipment Ltd) — PRODUCTION customer tenant on .app zone.
    installName: "aquipe",
    brand: "aquipe",
    authProvider: "gcip",
    gcipApiKey: "AIzaSyCCN4FKi9dWpqTNFO9KpLRnWVMFJzBmBXI",
    gcipAuthDomain: "aeronet-agent.firebaseapp.com",
    redirectSignIn: "https://aquipe.aeronet.app/app/",
    redirectSignOut: "https://aquipe.aeronet.app/app/?function=log_out",
    apiUrl: "https://api.aquipe.aeronet.app",
  },
  mustang: {
    // Mustang Helicopters — Flight & Duty demo tenant on .app zone.
    installName: "mustang",
    demo: true,
    hubLabel: "Mustang Hub",
    authProvider: "gcip",
    gcipApiKey: "AIzaSyCCN4FKi9dWpqTNFO9KpLRnWVMFJzBmBXI",
    gcipAuthDomain: "aeronet-agent.firebaseapp.com",
    redirectSignIn: "https://mustang.aeronet.app/app/",
    redirectSignOut: "https://mustang.aeronet.app/app/?function=log_out",
    apiUrl: "https://api.mustang.aeronet.app",
    // NO dataViewUrl — deliberately (2026-09-25). 9493f25fb copied g2's value
    // here, which pointed Mustang's reports at `aeronet-data-views-g2`, a
    // service that reads module_g2_demo: Mustang users would have been shown
    // G2's figures. With none set, getDataViewUrl() returns undefined and the
    // reports surface says it isn't configured. Add a Mustang DataViews service
    // before adding a URL here.
  },
  peak: {
    // Peak Aviation — a prospect DEMO tenant showing Peak its own AMS data
    // migrated to v6. Per-tenant model like g2: own database
    // (module_peak_demo) + own API service (aeronet-fast-api-peak), so its API
    // base is set explicitly (no api.peak.aeronet.dev).
    installName: "peak",
    // Aaron 2026-09-26: "for auth use gcs for now same as mustang". GCIP on
    // the aeronet-agent project with no GCIP tenant: the same public key and
    // auth domain as mustang. peak.aeronet.dev must be added to the GCIP
    // authorized domains and to this key's referrer list (a live step, P2b).
    // Users come from repo/scripts/install/gcip_provision_user.py, never from
    // AMS `accountmanager`.
    authProvider: "gcip",
    gcipApiKey: "AIzaSyCCN4FKi9dWpqTNFO9KpLRnWVMFJzBmBXI",
    gcipAuthDomain: "aeronet-agent.firebaseapp.com",
    redirectSignIn: "https://peak.aeronet.dev/app/",
    redirectSignOut: "https://peak.aeronet.dev/app/?function=log_out",
    apiUrl: PEAK_API_URL_P2B_PREDICTED,
    // NO dataViewUrl, for the reason given on mustang: a copied g2 value
    // would show Peak G2's figures. NO hubLabel: g2's "G2 Hub" was Aaron's
    // call for g2 alone.
  },
  synergy: {
    // Synergy Aviation (Volatus Aerospace). Its own v6 tenant for the shared
    // Pilot + Aircraft Rosters, demo first on .dev (Aaron 2026-10-10:
    // "synergy.aeronet.dev, demo first"). Per-tenant model like peak: own
    // database (module_synergy_v6) + own API service (aeronet-fast-api-synergy),
    // reached at its run.app URL (no api.synergy.aeronet.dev).
    installName: "synergy",
    // Aaron 2026-10-11: "use cogneto from prod" = Cognito, not GCIP. synergy
    // shares its LIVE install's pool (synergy-prod / aeronet-synergy, us-east-1)
    // so Synergy staff can sign in with their synergy.aeronet.nz credentials,
    // the gch/volcanic/g2 pattern, NOT the shared survivor pool. Dedicated .dev
    // app client aeronet-fast-api-synergy-dev (created 2026-10-11); the live
    // .nz client is untouched. No cognitoIdentityPoolId: like gch/volcanic,
    // nothing on this tenant needs federated AWS credentials.
    userPoolId: "us-east-1_47jlJ6C0E",
    cognitoDomain: "aeronet-synergy.auth.us-east-1.amazoncognito.com",
    cognitoClientId: "5sv4008qsh9c6skb7at7nsa3ig",
    cognitoRegion: "us-east-1",
    redirectSignIn: "https://synergy.aeronet.dev/app/",
    redirectSignOut: "https://synergy.aeronet.dev/app/?function=log_out",
    apiUrl: SYNERGY_API_URL_PREDICTED,
    // NO dataViewUrl and NO hubLabel, for the reasons given on mustang and peak.
  },
};

// Apex (aeronet.dev) and any unrecognised host fall back to demo. The apex is
// served by the separate GCS bucket, not this image, so this only matters for
// local dev / unexpected hosts.
const DEFAULT_PRODUCT: Product = "demo";

const isProduct = (v: string): v is Product =>
  (PRODUCTS as readonly string[]).includes(v);

let _cached: Product | null = null;

function resolveProduct(): Product {
  // 1. Hostname: "<product>.aeronet.dev" or "<product>.aeronet.app" -> product.
  // Must take precedence over build-time env vars in production environments.
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    const label = host.split(".")[0];
    if (isProduct(label)) return label;
  }

  // 2. Explicit build-time override (local dev / tests). Legacy: "aeronet"
  //    was the SCM tag, so map it back to scm.
  const override = (
    process.env.REACT_APP_PRODUCT ||
    process.env.REACT_APP_CUSTOMER ||
    ""
  )
    .trim()
    .toLowerCase();
  if (override) {
    if (override === "aeronet") return "scm";
    if (isProduct(override)) return override;
  }

  // 3. Apex / unknown host.
  return DEFAULT_PRODUCT;
}

/** The active product for this page, resolved once and cached. */
export function getProduct(): Product {
  if (!_cached) _cached = resolveProduct();
  return _cached;
}

export function getProductConfig(): ProductConfig {
  return PRODUCT_CONFIG[getProduct()];
}

/**
 * Backend API base, e.g. https://api.scm.aeronet.dev.
 * Local dev / tests override via REACT_APP_API_URL (e.g. http://localhost:9999).
 */
export function getApiUrl(): string {
  // Local vibe stack: *.localhost pages are served through Traefik, which
  // routes the same-origin /v6 prefix to the local backend. A localhost page
  // must never target a live API host (CORS-blocked, and wrong data anyway),
  // so return "" = relative/same-origin. No production tenant host is
  // *.localhost, so this branch is unreachable on live.
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.endsWith(".localhost"))
  ) {
    return process.env.REACT_APP_API_URL || "";
  }
  if (
    process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.includes("localhost")
  ) {
    return process.env.REACT_APP_API_URL;
  }
  return (
    getProductConfig().apiUrl ||
    process.env.REACT_APP_API_URL ||
    liveApiUrlFor(getProduct())
  );
}

// ── Aeronet Mobile's tenants (g2 mobile, 2026-10-13) ────────────────────────
// public/mobile is plain JavaScript with no build step. It used to hard-code
// ONE host (`mustang` → its API; every other host "isn't enabled"). It now
// reads this table through the GENERATED public/mobile/js/tenants.js
// (scripts/build-mobile-tenants.js), so a tenant added above reaches the phone
// when that file is regenerated — src/mobile/__tests__/mobileTenants.test.js
// fails while it is stale. Only the API base and the identity provider leave
// this file: never a client id or a key (gitleaks allowlists this file only).
// Which tenants SHOW Mobile is data — their "Mobile" nav rows (mod_function
// `mobile-emulator`) — not a hostname.

/** A product's live API base: its explicit `apiUrl`, else
 *  https://api.<product>.aeronet.dev. getApiUrl's answer on a live host (the
 *  Cloud Run image sets no REACT_APP_API_URL). */
export function liveApiUrlFor(product: Product): string {
  return PRODUCT_CONFIG[product].apiUrl || `https://api.${product}.aeronet.dev`;
}

export interface MobileTenant {
  apiUrl: string;
  authProvider: AuthProvider;
}

/** Every product's API base and identity provider, keyed by the hostname's
 *  first label — what getApiUrl() and getAuthProvider() answer on that host. */
export function mobileTenants(): Record<Product, MobileTenant> {
  const out = {} as Record<Product, MobileTenant>;
  PRODUCTS.forEach((p) => {
    out[p] = { apiUrl: liveApiUrlFor(p), authProvider: PRODUCT_CONFIG[p].authProvider ?? "cognito" };
  });
  return out;
}

/**
 * Data Views API base.
 * Local vibe stack: *.localhost pages route /data/view through Traefik, so return "" (relative).
 * Live tenants: resolves from productConfig.dataViewUrl or REACT_APP_DATA_VIEW_URL.
 */
export function getDataViewUrl(): string | undefined {
  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.endsWith(".localhost"))
  ) {
    return process.env.REACT_APP_DATA_VIEW_URL || "/data/view";
  }
  if (process.env.REACT_APP_DATA_VIEW_URL) {
    return process.env.REACT_APP_DATA_VIEW_URL.replace(/\/+$/, "");
  }
  const config = getProductConfig();
  if (config?.dataViewUrl) {
    return config.dataViewUrl.replace(/\/+$/, "");
  }
  return undefined;
}

/** X-Install-Name header value (SCM = "aeronet"). */
export function getInstallName(): string {
  return getProductConfig().installName;
}

/** The brand this tenant renders under. Defaults to the Aeronet identity. */
export function getBrand(): Brand {
  return BRANDS[getProductConfig().brand ?? "aeronet"];
}

/** Which identity provider backs this tenant's login. Defaults to Cognito. */
export function getAuthProvider(): AuthProvider {
  return getProductConfig().authProvider ?? "cognito";
}

/**
 * Whether this tenant is a pure v6 installation with NO legacy Roxen backend.
 * Pure v6 tenants must route every nav item to its React /app/<module> view.
 */
export function isPureV6Tenant(): boolean {
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (host.includes("localhost") || host.includes("127.0.0.1")) {
      return true;
    }
  }
  if (process.env.REACT_APP_ENV === "local") {
    return true;
  }
  const prod = getProduct();
  return (
    getAuthProvider() === "gcip" ||
    prod === "g2" ||
    prod === "demo" ||
    prod === "gch" ||
    prod === "volcanic" ||
    prod === "aquipe" ||
    prod === "peak" ||
    prod === "synergy"
  );
}

/**
 * GCIP config for this tenant. Throws for a Cognito tenant — callers must gate
 * on getAuthProvider() first, so a mis-wired tenant fails loudly at boot rather
 * than silently rendering an unauthenticated app.
 */
export function getGcipConfig() {
  const cfg = getProductConfig();
  if (cfg.authProvider !== "gcip") {
    throw new Error(
      `getGcipConfig() called for "${getProduct()}", which is on Cognito`
    );
  }
  return {
    apiKey: process.env.REACT_APP_GCIP_API_KEY || cfg.gcipApiKey,
    authDomain: process.env.REACT_APP_GCIP_AUTH_DOMAIN || cfg.gcipAuthDomain,
    tenantId: process.env.REACT_APP_GCIP_TENANT_ID || cfg.gcipTenantId,
    redirectSignIn: cfg.redirectSignIn,
    redirectSignOut: cfg.redirectSignOut,
  };
}

/**
 * Amplify Auth config for this product (env vars still win for local dev).
 * Cognito tenants only — see getGcipConfig() for the GCIP half.
 */
export function getCognitoConfig() {
  const cfg = getProductConfig();
  if (cfg.authProvider === "gcip") {
    throw new Error(
      `getCognitoConfig() called for "${getProduct()}", which is on GCIP`
    );
  }
  return {
    region: cfg.cognitoRegion || process.env.REACT_APP_REGION || COGNITO_REGION,
    userPoolId: cfg.userPoolId || process.env.REACT_APP_COGNITO_USER_POOL_ID || COGNITO_USER_POOL_ID,
    userPoolWebClientId:
      cfg.cognitoClientId || process.env.REACT_APP_COGNITO_USER_POOL_WEB_CLIENT_ID,
    identityPoolId:
      cfg.cognitoIdentityPoolId ||
      process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID ||
      undefined,
    domain: cfg.cognitoDomain || process.env.REACT_APP_COGNITO_DOMAIN || COGNITO_DOMAIN,
    redirectSignIn:
      process.env.REACT_APP_COGNITO_REDIRECT_SIGNIN || cfg.redirectSignIn,
    redirectSignOut:
      process.env.REACT_APP_COGNITO_REDIRECT_SIGNOUT || cfg.redirectSignOut,
  };
}
